import memoizeOne from "memoize-one";

/**
 * A data source wraps a data structure and makes it iterable and clonable.
 *
 * @remarks
 *
 * There are two constraints for the underlying item of a data source:
 * - items may not be {undefined}
 * - for each item, it should be possible to return a string uniquely identifying the item
 */
interface DataSource<ITEM> {
    /**
     * Returns an iterator over the items of the data source.
     */
    iter(): DataIterator<ITEM>;

    /**
     * Given an item, return a unique identifier for this item.
     */
    getId: (item: ITEM) => string;

    /**
     * Clones the data source.
     */
    clone(): DataSource<ITEM>;

    /**
     * Clones and filter the data source.
     */
    filtered(predicate: (item: ITEM) => boolean): DataSource<ITEM>;

    /**
     * Clones and filter the data source by item IDs.
     */
    filteredByIds(
        excludedIds: Set<string>,
        includedIds: Set<string>
    ): DataSource<ITEM>;
}

/**
 * An iterator over the items of a data source. Returns {undefined}
 */
interface DataIterator<ITEM> {
    /**
     * Returns the next item, or {undefined} if the iterator is exhausted.
     */
    next: () => ITEM | undefined;
    /**
     * The number of items in the iterator.
     */
    length: () => number;
}

/**
 * A data source based upon an array.
 *
 * @remarks
 *
 * The underlying array should NOT be modified once given to the data source.
 *
 * An example:
 *
 * ```typescript
 * const items: Array<{id: number, name: string}> = [{id: 1, name: "Jane"}, {id: 2, name: "John"}];
 * const dataSource = new ArrayDataSource(items, item => item.id.toString());
 *
 * const iter = dataSource.iter();
 * console.log(iter.next().name); // Jane
 * console.log(iter.next().name); // John
 * ```
 */
class ArrayDataSource<ITEM> implements DataSource<ITEM> {
    readonly getId: (item: ITEM) => string;
    private readonly items: Array<ITEM>;
    private readonly filters: Array<(item: ITEM) => boolean>;

    constructor(
        items: Array<ITEM>,
        getId: (item: ITEM) => string,
        filters?: Array<(item: ITEM) => boolean>
    ) {
        this.items = items;
        this.getId = getId;
        this.filters = filters || [];
    }

    iter() {
        return new ArrayDataIterator<ITEM>(this.items, this.filters);
    }

    clone() {
        return new ArrayDataSource<ITEM>(this.items, this.getId, this.filters);
    }

    filtered(predicate: (item: ITEM) => boolean): DataSource<ITEM> {
        return new ArrayDataSource<ITEM>(this.items, this.getId, [
            ...this.filters,
            predicate,
        ]);
    }

    filteredByIds(
        excludedIds: Set<string>,
        includedIds: Set<string>
    ): DataSource<ITEM> {
        // Note: the application of this.filters is handled by the caller, whenever necessary.
        // The use case is a call from the indexer, where filters are already applied (they must be).
        const newItems = this.items.filter((item) => {
            const id = this.getId(item);
            return !excludedIds.has(id) && includedIds.has(id);
        });
        return new ArrayDataSource(newItems, this.getId, undefined);
    }
}

/**
 * The iterator used by {ArrayDataSource}.
 */
class ArrayDataIterator<ITEM> implements DataIterator<ITEM> {
    readonly length: () => number;
    private readonly items: Array<ITEM>;
    private readonly filters: Array<(item: ITEM) => boolean>;
    private currentIndex: number;

    /**
     * @param items - The underlying array of items.
     * @param filters - List of predicates.
     */
    constructor(items: Array<ITEM>, filters: Array<(item: ITEM) => boolean>) {
        this.length = memoizeOne(
            () => this.items.filter(this.isMatched).length
        );
        this.items = items;
        this.filters = filters;
        this.currentIndex = 0;
    }

    next(): ITEM | undefined {
        while (this.currentIndex < this.items.length) {
            const item = this.items[this.currentIndex++];
            if (this.isMatched(item)) {
                return item;
            }
        }
        return undefined;
    }

    private isMatched = (item: ITEM) => this.filters.every((f) => f(item));
}

/**
 * A node descriptor.
 *
 * @remarks
 *
 * The {TreeDataSource} wraps the base value {NODE} into a {NodeDescriptor}.
 */
interface NodeDescriptor<NODE> {
    /** The node itself. */
    value: NODE;
    /**
     * List of ancestors of the node, starting from the root node. Empty if
     * this is the descriptor of the root node.
     */
    ancestors: Array<NODE>;
    /**
     * Lazy computed list of children of the node. Empty if
     * this is the descriptor of a leaf node.
     */
    children: () => Array<NODE>;
    /**
     * Index of the node in a depth-first traversal.
     */
    index: number;
    /**
     * Helper function to get an id from related nodes (like ancestors / children)
     * TODO: Rename / explain better !
     */
    getId: (node: NODE) => string;
}

/**
 * A data source wrapping multiple tree structures.
 *
 * @remarks
 *
 * This data source wraps the underlying data in a {NodeDescriptor}. In
 * addition of the usual {DataSource} constraints, nodes should provide a way
 * to get a string representation. The string representation should be usable
 * to display a path separated by /.
 */
class MultiTreeDataSource<NODE> implements DataSource<NodeDescriptor<NODE>> {
    /**
     * Retrieves the id of a node (not a node descriptor).
     */
    readonly baseGetId: (item: NODE) => string;
    /**
     * Retrieves the string representation of a node (not a node descriptor).
     */
    readonly baseGetString: (item: NODE, level: number) => string;
    private readonly roots: Array<NODE>;
    private readonly getChildren: (node: NODE) => Array<NODE>;
    private readonly length?: number;
    private readonly filters: Array<(node: NodeDescriptor<NODE>) => boolean>;

    /**
     * @param roots - the root nodes
     * @param getChildren - given a node, returns the list of children
     * @param getId - given a node, returns its unique id
     * @param getString - given a node and its depth level, returns its string representation
     * @param length - the number of nodes in the tree, if already known
     * @param filters - list of predicates, if any
     */
    constructor(
        roots: Array<NODE>,
        getChildren: (node: NODE) => Array<NODE>,
        getId: (item: NODE) => string,
        getString: (item: NODE, level: number) => string,
        filters?: Array<(node: NodeDescriptor<NODE>) => boolean>,
        length?: number
    ) {
        this.roots = roots;
        this.getChildren = getChildren;
        this.baseGetId = getId;
        this.baseGetString = getString;
        this.filters = filters || [];
        this.length = length;
    }

    getId(nodeDesc: NodeDescriptor<NODE>) {
        return this.baseGetId(nodeDesc.value);
    }

    iter() {
        return new TreeDataIterator<NODE>(
            this.roots,
            this.getChildren,
            this.baseGetId,
            this.filters,
            this.length
        );
    }

    clone() {
        return new MultiTreeDataSource<NODE>(
            this.roots,
            this.getChildren,
            this.baseGetId,
            this.baseGetString,
            this.filters,
            this.length
        );
    }

    filtered(
        predicate: (node: NodeDescriptor<NODE>) => boolean
    ): DataSource<NodeDescriptor<NODE>> {
        return new MultiTreeDataSource<NODE>(
            this.roots,
            this.getChildren,
            this.baseGetId,
            this.baseGetString,
            [...this.filters, predicate]
        );
    }

    filteredByIds(
        excludedIds: Set<string>,
        includedIds: Set<string>
    ): DataSource<NodeDescriptor<NODE>> {
        // This data source does not know, so far, how to clone nodes and modify children
        // of clones. So we have no choice but being lazy at this time.
        const keptIds: Set<string> = new Set();
        const discardedIds: Set<string> = new Set(excludedIds);
        forEach(this.iter(), (nodeDesc) => {
            const id = this.getId(nodeDesc);
            // Discarded explicitly.
            if (discardedIds.has(id)) {
                return;
            }
            // Discarded if parent discarded.
            // It works because the iterator returns nodes in order, parent first.
            if (nodeDesc.ancestors.length > 0) {
                const parentId = this.baseGetId(
                    nodeDesc.ancestors[nodeDesc.ancestors.length - 1]
                );
                if (discardedIds.has(parentId)) {
                    discardedIds.add(id);
                    return;
                }
            }
            // Included explicitly.
            if (includedIds.has(id)) {
                keptIds.add(id);
                // Keep path from root
                for (const ancestor of nodeDesc.ancestors) {
                    const ancestorId = this.baseGetId(ancestor);
                    keptIds.add(ancestorId);
                }
            } else {
                // Keep if any ancestor was kept because explicitly included only
                // Other kept IDs do not pull their branch for one reason: we actually
                // don't want them.
                //
                // For instance if a node is in includedIds, we want all of its ancestors,
                // but we do NOT want its siblings unless they are also included explicitly.
                //
                // Without the check on includedIds below we would find the siblings'
                // ancestors in keptIds and include the siblings as well. Moreover there
                // would be an incoherence between siblings appearing before or after newly
                // kept IDs and this would make the whole algorithm incorrect.
                for (const ancestor of nodeDesc.ancestors) {
                    const ancestorId = this.baseGetId(ancestor);
                    if (
                        keptIds.has(ancestorId) &&
                        includedIds.has(ancestorId)
                    ) {
                        keptIds.add(id);
                        break;
                    }
                }
            }
        });
        // Note: the application of this.filters is handled by the caller, whenever necessary.
        // The use case is a call from the indexer, where filters are already applied (they must be).
        return new MultiTreeDataSource(
            this.roots,
            this.getChildren,
            this.baseGetId,
            this.baseGetString,
            [
                (node) => {
                    const id = this.baseGetId(node.value);
                    return !discardedIds.has(id) && keptIds.has(id);
                },
            ]
        );
    }
}

/**
 * Iterates depth-first over nodes in the data source. Nodes are wrapped into {NodeDescriptor}.
 *
 * Note: TreeDataIterator perform a root filtering in the constructor, so construction without consumption should be avoid
 */
class TreeDataIterator<NODE> implements DataIterator<NodeDescriptor<NODE>> {
    readonly length: () => number;
    // Filtered list of roots
    private readonly roots: Array<NODE>;
    private readonly filters: Array<(node: NodeDescriptor<NODE>) => boolean>;
    private readonly getChildren: (node: NODE) => Array<NODE>;
    private readonly getId: (node: NODE) => string;
    // Next node to be yield, filtered
    private nextNode: NODE | undefined;
    // Ancestors of the current node
    private ancestors: Array<NODE>;
    // Siblings of the current node and ancestors
    private siblings: Array<Array<NODE>>;
    // For each ancestor + the current node, stores the position of the node in its list of siblings, starting with rootIndex
    private indexPath: Array<number>;
    // Node index, unique and incremental, but might not be continuous
    private index: number;
    private rootIndex: number;

    constructor(
        roots: Array<NODE>,
        getChildren: (node: NODE) => Array<NODE>,
        getId: (node: NODE) => string,
        filters: Array<(node: NodeDescriptor<NODE>) => boolean>,
        length?: number
    ) {
        this.length = memoizeOne(() =>
            length !== undefined
                ? length
                : toArray(
                      new TreeDataIterator(
                          this.roots,
                          this.getChildren,
                          this.getId,
                          this.filters
                      )
                  ).length
        );
        this.getChildren = getChildren;
        this.getId = getId;
        this.filters = filters;
        this.ancestors = [];
        this.siblings = [];
        this.indexPath = [0];
        this.index = 0;
        this.rootIndex = 0;
        // An index for root filtering,
        let rootFilterIndex = 0;
        this.roots = roots.filter((root) =>
            this.isMatched({
                value: root,
                ancestors: [],
                index: rootFilterIndex++,
                getId: this.getId,
                children: memoizeOne(() =>
                    this.getChildren(root).filter((c) =>
                        this.isMatched({
                            value: c,
                            ancestors: [root],
                            getId: this.getId,
                            index: rootFilterIndex++,
                            children: () => {
                                // Prevent depth tree traversal in filter
                                throw new Error(
                                    "Depth tree traversal in children is prohibited"
                                );
                            },
                        })
                    )
                ),
            })
        );
        this.nextNode = this.roots.length > 0 ? roots[0] : undefined;
    }

    next() {
        if (this.nextNode === undefined) {
            return undefined;
        }
        const currentNode = this.nextNode;
        const result: NodeDescriptor<NODE> = {
            value: currentNode,
            ancestors: this.ancestors,
            index: this.index,
            getId: this.getId,
            children: memoizeOne(() =>
                this.getChildren(currentNode).filter((c, i) =>
                    this.isMatched({
                        value: c,
                        ancestors: [...this.ancestors, currentNode],
                        getId: this.getId,
                        index: this.index + i + 1,
                        children: () => {
                            // Prevent depth tree traversal in filter
                            throw new Error(
                                "Depth tree traversal in children is prohibited"
                            );
                        },
                    })
                )
            ),
        };
        this.index++;

        let nextNode: NODE | undefined;
        const nextAncestors = this.ancestors.slice();
        let nextIndexPath = this.indexPath.slice();
        let nextSiblings = this.siblings.slice();
        // Depth-first traversal, only for valid nodes
        if (result.children().length > 0) {
            const children = result.children();
            nextNode = children[0];
            nextIndexPath.push(0);
            nextAncestors.push(currentNode);
            nextSiblings.push(children);
        } else {
            // No children for the current node, try to find a new node
            while (true) {
                if (nextAncestors.length === 0) {
                    this.rootIndex++;
                    if (this.rootIndex < this.roots.length) {
                        // No ancestor, move to next root
                        nextNode = this.roots[this.rootIndex];
                        nextIndexPath = [this.rootIndex];
                        nextSiblings = [];
                        break;
                    } else {
                        // No ancestor, we're at the last root, nothing left to do
                        nextNode = undefined;
                        nextIndexPath = [];
                        nextSiblings = [];
                        break;
                    }
                } else {
                    // No children, try next siblings
                    const indexOfCurrentNode =
                        nextIndexPath[nextIndexPath.length - 1];
                    const siblings = nextSiblings[nextSiblings.length - 1];
                    const newNodeIndex = indexOfCurrentNode + 1;
                    // Valid sibling
                    if (newNodeIndex < siblings.length) {
                        nextIndexPath[nextIndexPath.length - 1] = newNodeIndex;
                        nextNode = siblings[newNodeIndex];
                        break;
                    } else {
                        // No more siblings, go up a level
                        nextAncestors.pop();
                        nextIndexPath.pop();
                        nextSiblings.pop();
                    }
                }
            }
        }
        this.nextNode = nextNode;
        this.indexPath = nextIndexPath;
        this.ancestors = nextAncestors;
        this.siblings = nextSiblings;

        return result;
    }

    private isMatched = (node: NodeDescriptor<NODE>) =>
        this.filters.every((f) => f(node));
}

/**
 * Collects the content of an iterator into an array.
 */
function toArray<ITEM>(iter: DataIterator<ITEM>): Array<ITEM> {
    const itemsFromIter: Array<ITEM> = [];
    while (true) {
        const item = iter.next();
        if (item === undefined) {
            break;
        }
        itemsFromIter.push(item);
    }
    return itemsFromIter;
}

/**
 * Runs a callback over each item of an iterator.
 */
function forEach<ITEM>(
    iter: DataIterator<ITEM>,
    callback: (item: ITEM) => void
): void {
    while (true) {
        const item = iter.next();
        if (item === undefined) {
            break;
        }
        callback(item);
    }
}

/**
 * Finds the first item matching the predicate.
 */
function find<ITEM>(
    iter: DataIterator<ITEM>,
    predicate: (item: ITEM) => boolean
): ITEM | undefined {
    let item = iter.next();
    while (item) {
        if (predicate(item)) {
            return item;
        }
        item = iter.next();
    }
    return undefined;
}

export {
    type DataSource,
    type DataIterator,
    ArrayDataSource,
    ArrayDataIterator,
    MultiTreeDataSource,
    TreeDataIterator,
    type NodeDescriptor,
    toArray,
    forEach,
    find,
};
